import { message } from "antd";
import S3 from "aws-sdk/clients/s3";
import * as bb from "bluebird";
import strings from "i18n/strings";
import generate from "nanoid/generate";
import api from "./api";

const CREDENTIALS_REFRESH_BUFFER_MS = 5 * 60 * 1000;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isExpiredTokenError(error) {
  return (
    error &&
    (error.code === "ExpiredToken" ||
      error.name === "ExpiredToken" ||
      /token has expired/i.test(error.message || ""))
  );
}

export default async (
  authorId,
  matterId,
  fileList,
  onProgress,
  onFatalError
) => {
  window.onbeforeunload = function () {
    return strings.interruptUpload;
  };
  let uploadCount = 0;
  let uploadedFiles = [];
  try {
    var uploadInstance = await api.createUpload(matterId, fileList.length);
  } catch (error) {
    onFatalError(error);
  }

  let s3;
  let credentialsExpiration = 0;
  let credentialsRefresh = null;
  async function refreshS3Credentials() {
    if (credentialsRefresh) return credentialsRefresh;

    credentialsRefresh = api
      .getS3Credentials(uploadInstance.id)
      .then((s3Credentials) => {
        credentialsExpiration = s3Credentials.Expiration
          ? new Date(s3Credentials.Expiration).getTime()
          : 0;

        if (Number.isNaN(credentialsExpiration)) credentialsExpiration = 0;

        s3 = new S3({
          region: "eu-west-1",
          accessKeyId: s3Credentials.AccessKeyId,
          secretAccessKey: s3Credentials.SecretAccessKey,
          sessionToken: s3Credentials.SessionToken,
        });
      });

    try {
      await credentialsRefresh;
    } finally {
      credentialsRefresh = null;
    }
  }

  async function ensureFreshS3Credentials() {
    if (
      !s3 ||
      (credentialsExpiration &&
        Date.now() + CREDENTIALS_REFRESH_BUFFER_MS >= credentialsExpiration)
    ) {
      await refreshS3Credentials();
    }
  }

  await refreshS3Credentials();
  const credentialsUpdateInterval = setInterval(() => {
    refreshS3Credentials().catch((error) => {
      // Upload attempts also refresh credentials before use; this only keeps
      // the background refresh from creating unhandled promise rejections.
      console.warn("Failed to refresh S3 upload credentials", error);
    });
  }, 1000 * 60 * 55); // 5 minutes before expiration
  return bb.resolve(fileList).map(
    async function uploadFile(file) {
      try {
        await ensureFreshS3Credentials();
        await s3
          .upload({
            Bucket: process.env.REACT_APP_S3_BUCKET,
            Key: `batch-upload/${authorId}/${uploadInstance.id}/${generate(
              "1234567890abcdef",
              15
            )}.jpg`,
            Body: file,
          })
          .promise();
      } catch (error) {
        if (isExpiredTokenError(error)) {
          try {
            await refreshS3Credentials();
          } catch (refreshError) {
            console.warn(
              "Failed to refresh expired S3 upload credentials",
              refreshError
            );
          }
        }
        message.warn(
          `${strings.errorWhileUploadingFile} ${file.name}: ${error.message}. ${strings.restartIn}`
        );
        await delay(10000);
        return uploadFile(file);
      }
      uploadCount++;
      uploadedFiles = uploadedFiles.concat([file]);
      onProgress(
        Math.ceil((uploadCount / fileList.length) * 100),
        uploadedFiles
      );
      if (uploadCount === fileList.length) {
        window.onbeforeunload = null;
        clearInterval(credentialsUpdateInterval);
        try {
          await api.finishUpload(uploadInstance.id);
        } catch (error) {
          onFatalError(error);
        }
      }
    },
    { concurrency: 10 }
  );
};
